import { RequirePermission } from "@components/common/require-permission";
import { Box } from "@mui-wrapper";
import { useState } from "react";
import { useAppParams } from "@router/router-helper";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { EmailUtils } from "@stellar/utils";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { FaroCoreApiTypes, FaroCoreApiUtils } from "@stellar/core-api-client";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { WorkspaceEvents } from "@utils/track-event/track-event-list";
import { BaseCompanyProps } from "@custom-types/base-types";
import { useAppDispatch } from "@store/store-helper";
import { fetchCompany } from "@store/workspace/workspace-thunks";
import { TextWithTooltip } from "@components/common/text-with-tooltip";
import {
  Alert,
  Button,
  Dialog,
  Dropdown,
  FontWeights,
  TextField,
  useToast,
} from "@flat-ui";

/** Allowed roles to invite member with */
const allowedRolesToInvite: FaroCoreApiTypes.AddMemberToCompanyRole[] = [
  "enterprise_admin",
  "enterprise_viewer",
];

/** Component to handle invite member to workspace */
export function InviteMemberToWorkspace({
  company,
}: BaseCompanyProps): React.JSX.Element {
  const { companyId } = useAppParams();
  const { handleErrorWithToast } = useErrorHandler();
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();

  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>("");
  const [isEmailTouched, setIsEmailTouched] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] =
    useState<FaroCoreApiTypes.AddMemberToCompanyRole>("enterprise_admin");

  const { openToast } = useToast();

  const isEmailValid = EmailUtils.isValidEmail(email);
  const shouldShowError = isEmailTouched && !isEmailValid;

  async function onConfirm(): Promise<void> {
    setIsEmailTouched(true);

    trackEvent({
      name: WorkspaceEvents.inviteMember,
      props: { role: selectedRole },
    });

    if (!isEmailValid) {
      return;
    }

    if (!companyId) {
      throw new Error("Workspace ID is not defined");
    }

    setIsLoading(true);

    try {
      const payload: FaroCoreApiTypes.AddMemberToCompanyPayload = {
        userIdentity: email,
        roles: [selectedRole],
      };

      await faroCoreApiClient.admin.addMemberToCompany({
        companyId,
        payload,
      });

      openToast({
        title: `Invitation as ${FaroCoreApiUtils.Workspace.formatUserWorkspaceRole(
          selectedRole
        )} sent to ${email}`,
      });

      // Refetch company to update members list
      await dispatch(fetchCompany({ companyId: company.id }));

      handleDialogClose();
    } catch (error) {
      handleErrorWithToast({
        id: `invite-user-${Date.now().toString()}`,
        title: "Could not invite user",
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  function handleEmailChange(text: string): void {
    setEmail(text);

    if (text.length === 0) {
      setIsEmailTouched(false);
    }
  }

  function handleDialogClose(): void {
    setIsDialogOpen(false);
    setEmail("");
    setSelectedRole("enterprise_admin");
    setIsLoading(false);
    setIsEmailTouched(false);
  }

  return (
    <Box>
      <RequirePermission permission="member.invite">
        <Button
          onClick={() => {
            trackEvent({ name: WorkspaceEvents.openInviteMemberToWorkspace });
            setIsDialogOpen(true);
          }}
        >
          Invite member
        </Button>
      </RequirePermission>

      <Dialog
        open={isDialogOpen}
        title="Invite member to the workspace"
        onCancel={handleDialogClose}
        onConfirm={onConfirm}
        confirmText="Send Invite"
        cancelText="Cancel"
        showSpinner={isLoading}
        isConfirmDisabled={shouldShowError || email.length === 0}
      >
        <Alert
          variant="info"
          sx={{ mb: 3 }}
          title={
            <TextWithTooltip
              prefix="Type an email to send an invitation to the workspace "
              text={company?.name}
              suffix=". Please double check the email address before submission because the invitation cannot be revoked in the AdminPanel"
              customSx={{ fontWeight: FontWeights.Bold }}
            />
          }
        />

        <TextField
          fullWidth
          autoFocus
          label="Email address"
          type="email"
          text={email}
          onTextChanged={handleEmailChange}
          onBlur={() => {
            if (email.length > 0) {
              setIsEmailTouched(true);
            }
          }}
          error={shouldShowError ? "Please enter a valid email address" : ""}
          placeholder="user@example.com"
          controlStyle={{ my: 2 }}
        />

        <Dropdown
          fullWidth
          label="Select role"
          value={selectedRole}
          onChange={(event) => setSelectedRole(event.target.value)}
          options={allowedRolesToInvite.map((role) => ({
            key: role,
            value: role,
            label: FaroCoreApiUtils.Workspace.formatUserWorkspaceRole(role),
          }))}
        />
      </Dialog>
    </Box>
  );
}
